import { useEffect } from 'react';
import { X, LogOut, MonitorSmartphone, Sun, Moon } from 'lucide-react';
import { useSettingsModal } from './settings-store';
import { useAuth } from '../auth/useAuth';
import { useTheme, type Theme } from './theme';
import { useCanvasGrid, gridLayers, GRID_KINDS, type GridKind } from './canvas-grid';
const GRID_LABEL: Record<GridKind, string> = {
  line: 'Lines', dot: 'Dots', blueprint: 'Blueprint', iso: 'Isometric', cross: 'Crosshatch', none: 'None',
};
const THEMES: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'system', label: 'System', Icon: MonitorSmartphone },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
];

/**
 * Settings + account overlay (account identity, theme, canvas grid). Opened from
 * the top-left ProfileMenu. The canvas-grid picker lives here now (out of the
 * action pill).
 */
export function SettingsModal() {
  const open = useSettingsModal((s) => s.open);
  const setOpen = useSettingsModal((s) => s.setOpen);
  const email = useAuth((s) => s.email);
  const signOut = useAuth((s) => s.signOut);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const grid = useCanvasGrid((s) => s.kind);
  const setGrid = useCanvasGrid((s) => s.setKind);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  if (!open) return null;
  const handle = email ? email.split('@')[0] : 'you';

  return (
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <div
        data-anim
        className="cf-pop-in w-full max-w-[460px] rounded-[14px] border border-[var(--color-menu-border)] bg-[var(--color-menu-bg)] p-5 shadow-[var(--shadow-2)]"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[16px] font-semibold text-[var(--color-text)]">Settings</h2>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close settings"
            className="grid h-8 w-8 place-items-center rounded-[8px] text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Account */}
        <Section label="Account">
          <div className="flex items-center justify-between gap-3">
            <span className="min-w-0 leading-tight">
              <b className="block text-[14px] text-[var(--color-text)]">{handle}</b>
              <span className="block truncate text-[13px] text-[var(--color-text-muted)]">{email ?? 'local dev'}</span>
            </span>
            <button
              onClick={() => { setOpen(false); signOut(); }}
              className="flex items-center gap-1.5 rounded-[8px] border border-[var(--color-border)] px-2.5 py-1.5 text-[13px] text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </Section>

        {/* Theme */}
        <Section label="Theme">
          <div className="flex gap-2">
            {THEMES.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                aria-pressed={theme === value}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-[8px] border px-2 py-2 text-[13px] ${
                  theme === value
                    ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-menu-hover)]'
                }`}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </Section>

        {/* Canvas grid */}
        <Section label="Canvas grid">
          <div className="grid grid-cols-3 gap-2">
            {GRID_KINDS.map((k) => (
              <button
                key={k}
                onClick={() => setGrid(k)}
                aria-pressed={grid === k}
                title={GRID_LABEL[k]}
                className="flex flex-col items-center gap-1.5"
              >
                <span
                  className="block h-[48px] w-full rounded-[7px] border"
                  style={{
                    ...gridLayers(k, 12, 0, 0),
                    borderColor: grid === k ? 'var(--color-accent)' : 'var(--color-border)',
                    outline: grid === k ? '2px solid var(--color-accent)' : 'none',
                    outlineOffset: -1,
                  }}
                />
                <span className="text-[12px] text-[var(--color-text-muted)]">{GRID_LABEL[k]}</span>
              </button>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">{label}</div>
      {children}
    </div>
  );
}
